import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Target, Rocket, Users, Heart, Lightbulb, Award,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { BRAND } from "../data/site";

const VALUES = [
  { icon: Heart, title: "Client-First", desc: "Every decision starts with what's right for your business, not our bottom line." },
  { icon: Lightbulb, title: "Practical Innovation", desc: "We use modern tools and AI where it actually moves the needle — not because it sounds trendy." },
  { icon: Award, title: "Craftsmanship", desc: "We take pride in delivering work we'd happily show off — clean design, solid code, sharp copy." },
  { icon: Users, title: "Real Partnership", desc: "Direct communication, honest advice, and long-term support beyond project delivery." },
];

export default function About() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-[#0A0A0A] text-white pt-28 pb-24">
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute -top-24 -left-32 h-[440px] w-[440px] rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="absolute top-40 -right-32 h-[420px] w-[420px] rounded-full bg-[#F5D67B]/10 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">About Us</p>
            <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.05]">
              A digital agency that{" "}
              <span className="text-gradient-brand">grows with you.</span>
            </h1>
            <p className="mt-6 text-lg text-gray-300 max-w-xl leading-relaxed">
              {BRAND.name} is a modern digital agency helping small and growing businesses build a strong online presence — from websites and branding to Google visibility, social media and AI-powered automation.
            </p>
            <p className="mt-4 text-lg text-gray-300 max-w-xl leading-relaxed">
              We work as an extension of your team: understanding your business, listening to your goals, and delivering practical solutions that actually attract customers.
            </p>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden border border-white/10 glass p-4">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80"
                alt="Team collaboration"
                className="w-full h-96 object-cover rounded-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission / Vision */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid md:grid-cols-2 gap-6">
          <div data-testid="about-mission" className="rounded-2xl border border-white/10 bg-[#121212] p-8 md:p-10">
            <div className="h-12 w-12 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center mb-5">
              <Target className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Our Mission</h2>
            <p className="text-gray-300 leading-relaxed">
              To make premium digital growth accessible for every ambitious business — with clear communication, transparent process, and real business results.
            </p>
          </div>
          <div data-testid="about-vision" className="rounded-2xl border border-white/10 bg-[#121212] p-8 md:p-10">
            <div className="h-12 w-12 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center mb-5">
              <Rocket className="h-6 w-6 text-[#D4AF37]" />
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">Our Vision</h2>
            <p className="text-gray-300 leading-relaxed">
              To become the most trusted growth partner for local and emerging businesses — helping them Build, Market, and Scale online, one meaningful project at a time.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-[#121212] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="max-w-3xl mb-12">
            <p className="text-sm font-semibold tracking-[0.25em] uppercase text-[#D4AF37] mb-3">What We Stand For</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight">
              Values that guide every project
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {VALUES.map((v, i) => (
              <div key={i} data-testid={`about-value-${i}`} className="rounded-2xl border border-white/10 bg-[#121212] p-7 hover:border-[#D4AF37]/40 transition">
                <div className="h-11 w-11 rounded-xl bg-gradient-brand-soft border border-white/10 flex items-center justify-center mb-4">
                  <v.icon className="h-5 w-5 text-[#D4AF37]" />
                </div>
                <h3 className="font-display text-lg font-semibold mb-2">{v.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-5 lg:px-8">
          <div className="rounded-3xl bg-gradient-brand p-10 md:p-12 text-center glow-blue">
            <h3 className="font-display text-3xl md:text-4xl font-bold text-[#0A0A0A] mb-3">
              Ready to work together?
            </h3>
            <p className="text-black/70 mb-7 max-w-2xl mx-auto">
              Let's talk about your business and how we can help you grow online.
            </p>
            <Link to="/contact" data-testid="about-cta-button">
              <Button className="bg-white text-[#0A0A0A] hover:bg-[#F5D67B]/20 rounded-full h-12 px-7 font-bold">
                Get Free Consultation <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
